import { type AriaAttributes, useState } from "react";

import {
  type BoxProps,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  Row,
  Column,
  ChakraAccordion,
  ChakraAccordionItem,
  ChakraAccordionButton,
  ChakraAccordionPanel,
  Box,
} from "@hightouch/htui";
import { Card, type CardProps } from "src/components/card";

export type AccordionCardProps = {
  children: React.ReactNode;
  header: React.ReactNode;
  /**
   * Optional content rendered full-width below the header row (still inside the
   * toggle button). Use for a taller collapsed summary so the chevron stays
   * aligned with the header rather than centering against the whole block.
   */
  subheader?: React.ReactNode;
  /** Controlled open state. Omit (optionally with `defaultOpen`) for uncontrolled mode. */
  isOpen?: boolean;
  /** Toggle handler. Required for controlled use; in uncontrolled mode it still
   *  fires after the internal state flips so callers can observe changes. */
  onToggle?: () => void;
  /** Uncontrolled initial open state (ignored when `isOpen` is provided). */
  defaultOpen?: boolean;
  isDisabled?: boolean;
  variant?: "default" | "thin";
  buttonProps?: Pick<AriaAttributes, "aria-label" | "aria-describedby">;
  /** Tints the header row, edge to edge. */
  headerBg?: BoxProps["bg"];
  /** Which way the chevron points while the card is open. @default "up" */
  openChevron?: "up" | "down";
} & CardProps;

export const AccordionCard = ({
  isOpen,
  onToggle,
  defaultOpen = false,
  children,
  header,
  subheader,
  isDisabled,
  variant = "default",
  buttonProps,
  headerBg,
  openChevron = "up",
  ...cardProps
}: AccordionCardProps) => {
  const padY = variant === "default" ? 4 : 2;
  const padX = variant === "default" ? 6 : 4;

  // Controlled when `isOpen` is supplied; otherwise self-manage open state.
  const isControlled = isOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const open = isControlled ? isOpen : internalOpen;

  const handleToggle = () => {
    if (!isControlled) setInternalOpen((prev) => !prev);
    onToggle?.();
  };

  return (
    <Card p={0} gap={0} overflow="hidden" {...cardProps}>
      <ChakraAccordion
        allowToggle
        index={open ? [0] : []}
        onChange={handleToggle}
      >
        <ChakraAccordionItem border="none" isDisabled={isDisabled}>
          <ChakraAccordionButton
            type="button"
            aria-label={open ? "Hide content" : "View content"}
            {...buttonProps}
            cursor={isDisabled ? "default" : "pointer"}
            pointerEvents={isDisabled ? "none" : "auto"}
            p={0}
            borderRadius="xl"
            _hover={{}}
            _disabled={{}}
            // Box shadow isn't visible because the card cuts it off, use an outline instead.
            _focusVisible={{
              outline: "1px solid !important",
              outlineColor: "teal.border !important",
              outlineOffset: "-2px !important",
            }}
          >
            <Column width="100%" bg={headerBg}>
              <Row
                justify="space-between"
                align="center"
                pt={padY}
                pb={subheader ? 0 : padY}
                px={padX}
                width="100%"
              >
                {header}
                {!isDisabled && (
                  <Box
                    ml={4}
                    as={
                      open
                        ? openChevron === "down"
                          ? ChevronDownIcon
                          : ChevronUpIcon
                        : ChevronRightIcon
                    }
                    boxSize={6}
                    color="text.tertiary"
                    flexShrink={0}
                  />
                )}
              </Row>
              {subheader && (
                <Box
                  pl={padX}
                  // Reserve the chevron's column (ml 4 + boxSize 6) so long
                  // truncated text stops before the icon rather than under it.
                  pr={isDisabled ? padX : padX + 10}
                  pb={padY}
                  width="100%"
                  textAlign="left"
                >
                  {subheader}
                </Box>
              )}
            </Column>
          </ChakraAccordionButton>
          <ChakraAccordionPanel
            // unmountOnExit works around a Chakra v2 + framer-motion >=11
            // incompatibility: Collapse leaves the closed panel at
            // display:none, so framer-motion measures the expand target
            // (height "auto") as 0 and snaps open instead of sliding.
            // Unmounting the closed panel mounts it fresh (display:block) on
            // expand, so the measurement and slide work.
            motionProps={{ animateOpacity: false, unmountOnExit: true }}
            p={0}
          >
            {children}
          </ChakraAccordionPanel>
        </ChakraAccordionItem>
      </ChakraAccordion>
    </Card>
  );
};
